import AddIcon from '@mui/icons-material/Add';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import type { AvailabilityRule } from '../types';
import { ALL_DAY_MINUTES, summarizeSchedule } from '../utils/schedule';

/** Sentinel scope id for the org-wide ("Clinic default") calendar — maps to a
 * rule with `provider_id: null` when wired to the API. */
export const ORG_SCOPE_ID = 'org';
/** Sentinel option value for the "All types" (untyped, `encounter_type_id:
 * null`) slice — MUI Select can't hold a real null value cleanly. */
const ALL_TYPES = '__all__';

export interface AvailabilityScopeOption {
  /** ORG_SCOPE_ID for the clinic default, or a clinician's user id. */
  id: string;
  label: string;
  kind: 'org' | 'clinician';
}

export interface EncounterTypeOption {
  id: string;
  name: string;
}

export interface AvailabilityManagerViewProps {
  scopes: AvailabilityScopeOption[];
  selectedScopeId: string;
  onScopeChange: (scopeId: string) => void;

  encounterTypes: EncounterTypeOption[];
  /** null = the "All types" (untyped) slice. */
  selectedEncounterTypeId: string | null;
  onEncounterTypeChange: (encounterTypeId: string | null) => void;

  /** Rules for the selected scope; the view narrows them by encounter type. */
  rules: AvailabilityRule[];
  isLoading?: boolean;
  hasError?: boolean;
  deletingId?: string | null;

  onAddHours: () => void;
  onAddBlock: () => void;
  onEdit: (rule: AvailabilityRule) => void;
  onDelete: (rule: AvailabilityRule) => void;
}

/** Presentational availability manager (Direction 1: scope picker + focused
 * editor). You pick whose hours you're editing (clinic default or one
 * provider) and, optionally, an appointment type; the card then shows just
 * that slice — open hours and blocks — with the precedence rules spelled out
 * so the "a clinician's own hours replace the clinic default" behaviour isn't
 * a surprise. Presentational only: a container supplies the scoped rules and
 * wires the add/edit/delete callbacks. */
export function AvailabilityManagerView({
  scopes,
  selectedScopeId,
  onScopeChange,
  encounterTypes,
  selectedEncounterTypeId,
  onEncounterTypeChange,
  rules,
  isLoading = false,
  hasError = false,
  deletingId = null,
  onAddHours,
  onAddBlock,
  onEdit,
  onDelete,
}: AvailabilityManagerViewProps) {
  const selectedScope =
    scopes.find(s => s.id === selectedScopeId) ?? scopes[0];
  const isOrgScope = selectedScope?.kind === 'org';
  const clinicianScopes = scopes.filter(s => s.kind === 'clinician');
  const selectedTypeName = encounterTypes.find(
    t => t.id === selectedEncounterTypeId,
  )?.name;

  // The management view edits one encounter-type slice at a time: "All types"
  // is the untyped rules; a specific type is the rules scoped to exactly it.
  const scopedRules = rules.filter(
    rule => (rule.encounter_type_id ?? null) === selectedEncounterTypeId,
  );
  const openRules = scopedRules.filter(rule => rule.effect === 'open');
  const blockRules = scopedRules.filter(rule => rule.effect === 'block');

  const handleScopeChange = (event: SelectChangeEvent) => {
    onScopeChange(event.target.value);
  };
  const handleTypeChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    onEncounterTypeChange(value === ALL_TYPES ? null : value);
  };

  return (
    <Card
      elevation={1}
      sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200' }}
    >
      <CardHeader
        avatar={<EventAvailableOutlinedIcon color="primary" />}
        title="Availability"
        slotProps={{ title: { variant: 'h6', fontWeight: 600 } }}
        subheader="Recurring open hours and blocks that drive self-serve booking."
      />
      <CardContent sx={{ pt: 0 }}>
        {hasError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Couldn’t load availability. Try refreshing.
          </Alert>
        )}

        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          sx={{ flexWrap: 'wrap', mb: 1.5 }}
        >
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="availability-scope-label">Whose hours</InputLabel>
            <Select
              labelId="availability-scope-label"
              label="Whose hours"
              value={selectedScope?.id ?? ORG_SCOPE_ID}
              onChange={handleScopeChange}
            >
              <MenuItem value={ORG_SCOPE_ID}>Clinic default</MenuItem>
              {clinicianScopes.length > 0 && (
                <ListSubheader>Clinicians</ListSubheader>
              )}
              {clinicianScopes.map(scope => (
                <MenuItem key={scope.id} value={scope.id}>
                  {scope.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="availability-type-label">
              Appointment type
            </InputLabel>
            <Select
              labelId="availability-type-label"
              label="Appointment type"
              value={selectedEncounterTypeId ?? ALL_TYPES}
              onChange={handleTypeChange}
            >
              <MenuItem value={ALL_TYPES}>All types</MenuItem>
              {encounterTypes.map(type => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isOrgScope
            ? 'Applies to every clinician who hasn’t set their own hours.'
            : `${selectedScope?.label}’s own open hours replace the clinic default for them. Blocks add on top.`}
        </Typography>

        {selectedEncounterTypeId !== null && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            You’re editing hours specific to {selectedTypeName ?? 'this type'}.
            Your “All types” hours also apply to {selectedTypeName ?? 'it'} —
            switch back to <strong>All types</strong> to edit those.
          </Alert>
        )}

        {isLoading ? (
          <Stack spacing={1}>
            <Skeleton variant="rounded" height={40} />
            <Skeleton variant="rounded" height={40} />
            <Skeleton variant="rounded" height={40} />
          </Stack>
        ) : (
          <Stack spacing={1}>
            <RuleGroup
              title="Open hours"
              addLabel="Add hours"
              onAdd={onAddHours}
              rules={openRules}
              deletingId={deletingId}
              onEdit={onEdit}
              onDelete={onDelete}
              emptyText={
                isOrgScope
                  ? 'No clinic-default open hours yet.'
                  : `No personal hours — ${selectedScope?.label} uses the clinic default.`
              }
            />
            <Divider sx={{ my: 1 }} />
            <RuleGroup
              title="Blocks"
              addLabel="Add block"
              onAdd={onAddBlock}
              rules={blockRules}
              deletingId={deletingId}
              onEdit={onEdit}
              onDelete={onDelete}
              emptyText="No blocks."
              note={
                isOrgScope
                  ? 'A clinic-default block closes the calendar for every provider.'
                  : undefined
              }
            />
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

interface RuleGroupProps {
  title: string;
  addLabel: string;
  onAdd: () => void;
  rules: AvailabilityRule[];
  deletingId: string | null;
  emptyText: string;
  note?: string;
  onEdit: (rule: AvailabilityRule) => void;
  onDelete: (rule: AvailabilityRule) => void;
}

function RuleGroup({
  title,
  addLabel,
  onAdd,
  rules,
  deletingId,
  emptyText,
  note,
  onEdit,
  onDelete,
}: RuleGroupProps) {
  return (
    <Box>
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={onAdd}
          sx={{ textTransform: 'none' }}
        >
          {addLabel}
        </Button>
      </Stack>

      {note && (
        <Typography variant="caption" color="text.secondary">
          {note}
        </Typography>
      )}

      {rules.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          {emptyText}
        </Typography>
      ) : (
        <Stack sx={{ mt: 0.5 }}>
          {rules.map(rule => (
            <RuleRow
              key={rule.availability_rule_id ?? ''}
              rule={rule}
              isDeleting={deletingId === rule.availability_rule_id}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}

interface RuleRowProps {
  rule: AvailabilityRule;
  isDeleting: boolean;
  onEdit: (rule: AvailabilityRule) => void;
  onDelete: (rule: AvailabilityRule) => void;
}

function RuleRow({ rule, isDeleting, onEdit, onDelete }: RuleRowProps) {
  const isOpen = rule.effect === 'open';
  const summary = summarizeSchedule(rule);
  // Multi-day windows (calendar-created closures spanning a date range) have
  // no representation in the edit dialog — a save would silently collapse
  // them to one day (GDN-2481 review). Removing is still fine.
  const editableHere = rule.duration_minutes <= ALL_DAY_MINUTES;
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
        py: 0.75,
        borderBottom: 1,
        borderColor: 'grey.100',
      }}
    >
      <Chip
        size="small"
        icon={
          isOpen ? (
            <EventAvailableOutlinedIcon fontSize="small" />
          ) : (
            <BlockOutlinedIcon fontSize="small" />
          )
        }
        color={isOpen ? 'success' : 'default'}
        variant={isOpen ? 'filled' : 'outlined'}
        label={isOpen ? 'Open' : 'Block'}
      />
      <Typography variant="body2" sx={{ flex: 1 }}>
        {summary}
      </Typography>
      <Tooltip
        title={
          editableHere ? '' : 'Multi-day blocks are managed from the calendar'
        }
      >
        <span>
          <IconButton
            size="small"
            onClick={() => onEdit(rule)}
            disabled={!editableHere}
            aria-label={`Edit ${summary}`}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      {isDeleting ? (
        <CircularProgress size={16} aria-label="Removing" sx={{ mx: 1 }} />
      ) : (
        <IconButton
          size="small"
          onClick={() => onDelete(rule)}
          aria-label={`Remove ${summary}`}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      )}
    </Stack>
  );
}
